import * as React from 'react';
import { clsx } from 'clsx';

export interface SectionHeadingInteractiveProps {
  /**
   * The heading text (required)
   */
  title: string;
  /**
   * Content to display when expanded (sub-sections, list rows, etc.)
   */
  children?: React.ReactNode;
  /**
   * Whether the section is expanded (controlled mode)
   */
  expanded?: boolean;
  /**
   * Default expanded state (uncontrolled mode)
   * @default true
   */
  defaultExpanded?: boolean;
  /**
   * Callback when expanded state changes
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Chevron icon - rotates based on expanded state
const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease-in-out',
    }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#2f2f2f"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Arbor Design System section heading interactive styles
const sectionHeadingInteractiveStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: 'border-box' as const,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    textAlign: 'left' as const,
    outline: 'none',
  },
  // When collapsed, no bottom border
  headerContainerCollapsed: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  // When expanded, has bottom border
  headerContainerExpanded: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom: '1px solid #f8f8f8',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '12px 8px',
    borderRadius: '8px',
    transition: 'background-color 0.15s ease-in-out',
    minHeight: '16px',
    gap: '12px',
  },
  headerContentHover: {
    backgroundColor: '#f8f8f8',
  },
  headerContentFocus: {
    outline: '3px solid #3cad51',
    outlineOffset: '-3px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#2f2f2f',
    lineHeight: '1.25',
    margin: 0,
    flex: 1,
    whiteSpace: 'nowrap' as const,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  content: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease-in-out, opacity 0.2s ease-in-out',
  },
  contentExpanded: {
    maxHeight: '5000px',
    opacity: 1,
  },
  contentCollapsed: {
    maxHeight: '0px',
    opacity: 0,
  },
  contentInner: {
    // Content area styling - children go here
  },
};

/**
 * SectionHeadingInteractive component - Arbor Design System
 *
 * A collapsible section heading component that can contain sub-sections,
 * list rows, and other content. Click the header to expand/collapse.
 * Uses h2 heading style (22px) for main section level.
 *
 * @example
 * ```tsx
 * // Basic collapsible section
 * <SectionHeadingInteractive title="Personal Information">
 *   <SubSectionInteractive title="Contact Details">
 *     <ListRow label="Email" value="john@example.com" />
 *   </SubSectionInteractive>
 * </SectionHeadingInteractive>
 *
 * // Initially collapsed
 * <SectionHeadingInteractive title="Additional Settings" defaultExpanded={false}>
 *   <ListRow label="Theme" value="Dark" />
 * </SectionHeadingInteractive>
 *
 * // Controlled mode
 * <SectionHeadingInteractive
 *   title="Account"
 *   expanded={isExpanded}
 *   onExpandedChange={setIsExpanded}
 * >
 *   <ListRow label="Status" value="Active" />
 * </SectionHeadingInteractive>
 * ```
 */
export const SectionHeadingInteractive = React.forwardRef<HTMLDivElement, SectionHeadingInteractiveProps>(
  (
    {
      title,
      children,
      expanded: controlledExpanded,
      defaultExpanded = true,
      onExpandedChange,
      className,
      style,
      ...props
    },
    ref
  ) => {
    // Support both controlled and uncontrolled modes
    const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const isExpanded = isControlled ? controlledExpanded : internalExpanded;

    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    const handleToggle = () => {
      const newExpanded = !isExpanded;
      if (!isControlled) {
        setInternalExpanded(newExpanded);
      }
      onExpandedChange?.(newExpanded);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    };

    const headerContainerStyle: React.CSSProperties = isExpanded
      ? sectionHeadingInteractiveStyles.headerContainerExpanded
      : sectionHeadingInteractiveStyles.headerContainerCollapsed;

    const headerContentStyle: React.CSSProperties = {
      ...sectionHeadingInteractiveStyles.headerContent,
      ...(isHovered && sectionHeadingInteractiveStyles.headerContentHover),
      ...(isFocused && sectionHeadingInteractiveStyles.headerContentFocus),
    };

    const contentStyle: React.CSSProperties = {
      ...sectionHeadingInteractiveStyles.content,
      ...(isExpanded
        ? sectionHeadingInteractiveStyles.contentExpanded
        : sectionHeadingInteractiveStyles.contentCollapsed),
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-section-heading-interactive', className)}
        style={{ ...sectionHeadingInteractiveStyles.container, ...style }}
        {...props}
      >
        <div style={headerContainerStyle}>
          <button
            type="button"
            style={sectionHeadingInteractiveStyles.header}
            onClick={handleToggle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            aria-expanded={isExpanded}
          >
            <div style={headerContentStyle}>
              <h2 style={sectionHeadingInteractiveStyles.title}>{title}</h2>
              <span style={sectionHeadingInteractiveStyles.iconWrapper}>
                <ChevronIcon expanded={isExpanded} />
              </span>
            </div>
          </button>
        </div>
        <div style={contentStyle} aria-hidden={!isExpanded}>
          {children && (
            <div style={sectionHeadingInteractiveStyles.contentInner}>{children}</div>
          )}
        </div>
      </div>
    );
  }
);

SectionHeadingInteractive.displayName = 'SectionHeadingInteractive';
